import React, { useState } from "react";

const DEFAULT_EXCLUDED_SELECTORS = [
  "[data-download-exclude]",
  ".reference-check-signature-watermark",
];

const resolveTarget = (targetRef, targetSelector) =>
  targetRef?.current ||
  (targetSelector ? document.querySelector(targetSelector) : null);

const normalizeFilename = (filename, format) => {
  const extension = format === "png" ? "png" : "pdf";
  const safeName = String(filename || "document")
    .trim()
    .replace(/[<>:"/\\|?*]/g, "-")
    .replace(/\s+/g, "-")
    .replace(/\.(png|pdf)$/i, "");

  return `${safeName || "document"}.${extension}`;
};

const canvasToBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) =>
        result ? resolve(result) : reject(new Error("Unable to create the file.")),
      "image/png",
    );
  });

const downloadBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const fitPreservedMedia = (root, frameOptions) => {
  if (!frameOptions) return;

  const frameWidth = Number(frameOptions.width);
  const frameHeight = Number(frameOptions.height);
  const framePadding = Math.max(0, Number(frameOptions.padding) || 0);
  if (!frameWidth || !frameHeight) return;

  root.querySelectorAll("[data-download-media-frame]").forEach((frame) => {
    const media = frame.querySelector("[data-download-preserve-ratio]");
    if (!media) return;

    const sourceWidth =
      Number(media.naturalWidth) || Number(media.width) || media.clientWidth;
    const sourceHeight =
      Number(media.naturalHeight) || Number(media.height) || media.clientHeight;
    if (!sourceWidth || !sourceHeight) return;

    const availableWidth = Math.max(1, frameWidth - framePadding * 2);
    const availableHeight = Math.max(1, frameHeight - framePadding * 2);
    const fitScale = Math.min(
      availableWidth / sourceWidth,
      availableHeight / sourceHeight,
    );
    const renderedWidth = sourceWidth * fitScale;
    const renderedHeight = sourceHeight * fitScale;

    Object.assign(frame.style, {
      width: `${frameWidth}px`,
      height: `${frameHeight}px`,
      maxWidth: "100%",
      padding: `${framePadding}px`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      boxSizing: "border-box",
      background: "#ffffff",
      border: "1px solid #94a3b8",
      borderRadius: "4px",
      boxShadow: "none",
    });

    media.style.setProperty("width", `${renderedWidth}px`, "important");
    media.style.setProperty("height", `${renderedHeight}px`, "important");
    media.style.setProperty("max-width", "none", "important");
    media.style.setProperty("padding", "0", "important");
    media.style.setProperty("margin", "0", "important");
    media.style.setProperty("border", "0", "important");
    media.style.setProperty("border-radius", "0", "important");
    media.style.setProperty("box-shadow", "none", "important");
    media.style.setProperty("object-fit", "fill", "important");
    media.style.setProperty("transform", "none", "important");
  });
};

const canvasToPdfBlob = async ({
  canvas,
  filename,
  pageFormat,
  orientation,
  marginMm,
  footerText,
  showPageNumbers,
}) => {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({
    orientation,
    unit: "mm",
    format: pageFormat,
    compress: true,
  });
  pdf.setProperties({ title: filename.replace(/\.pdf$/i, "") });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - marginMm * 2;
  const contentHeight = pageHeight - marginMm * 2;
  const pageHeightPx = Math.max(
    1,
    Math.floor((canvas.width * contentHeight) / contentWidth),
  );
  const totalPages = Math.ceil(canvas.height / pageHeightPx);

  let pageIndex = 0;
  for (let sourceY = 0; sourceY < canvas.height; sourceY += pageHeightPx) {
    const sliceHeight = Math.min(pageHeightPx, canvas.height - sourceY);
    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = sliceHeight;
    const context = pageCanvas.getContext("2d");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    context.drawImage(
      canvas,
      0,
      sourceY,
      canvas.width,
      sliceHeight,
      0,
      0,
      canvas.width,
      sliceHeight,
    );

    if (pageIndex > 0) pdf.addPage(pageFormat, orientation);
    const renderedHeight = (sliceHeight * contentWidth) / canvas.width;
    pdf.addImage(
      pageCanvas.toDataURL("image/png"),
      "PNG",
      marginMm,
      marginMm,
      contentWidth,
      renderedHeight,
      undefined,
      "FAST",
    );
    const currentPage = pageIndex + 1;
    if (footerText || showPageNumbers) {
      const footerLineY = pageHeight - marginMm + 2;
      const footerTextY = footerLineY + 4;
      pdf.setDrawColor(203, 213, 225);
      pdf.setLineWidth(0.2);
      pdf.line(marginMm, footerLineY, pageWidth - marginMm, footerLineY);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(100, 116, 139);
      if (footerText) pdf.text(footerText, marginMm, footerTextY);
      if (showPageNumbers) {
        pdf.text(
          `Page ${currentPage} of ${totalPages}`,
          pageWidth - marginMm,
          footerTextY,
          { align: "right" },
        );
      }
    }
    pageIndex += 1;
  }

  return pdf.output("blob");
};

const DocumentDownloadButton = ({
  targetRef,
  targetSelector,
  filename = "document",
  label = "Download",
  className = "btn btn-outline-primary",
  format = "pdf",
  pageFormat = "a4",
  orientation = "portrait",
  marginMm = 12,
  footerText = "",
  showPageNumbers = true,
  captureWidth,
  capturePaddingPx = 40,
  preservedMediaFrame,
  scale = 2,
  backgroundColor = "#ffffff",
  excludeSelectors = DEFAULT_EXCLUDED_SELECTORS,
  disabled = false,
  onBeforeDownload,
  onClone,
  onDownloaded,
  onError,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (disabled || isDownloading) return;

    const target = resolveTarget(targetRef, targetSelector);
    if (!target) {
      const error = new Error("The document area is not available.");
      onError?.(error);
      if (!onError) window.alert(error.message);
      return;
    }

    try {
      setIsDownloading(true);
      await onBeforeDownload?.();
      await document.fonts?.ready;
      const { default: html2canvas } = await import("html2canvas");
      const outputFormat = String(format).toLowerCase() === "png" ? "png" : "pdf";
      const documentWidth =
        Number(captureWidth) || (orientation === "landscape" ? 1123 : 794);
      const selectors = Array.isArray(excludeSelectors)
        ? excludeSelectors.filter(Boolean)
        : [];

      const canvas = await html2canvas(target, {
        scale,
        backgroundColor,
        useCORS: true,
        logging: false,
        windowWidth: documentWidth,
        ignoreElements: (element) =>
          selectors.some((selector) => element.matches?.(selector)),
        onclone: (clonedDocument, clonedTarget) => {
          clonedTarget.classList.remove("slide-narrow");
          Object.assign(clonedTarget.style, {
            width: `${documentWidth}px`,
            maxWidth: "none",
            minWidth: "0",
            height: "auto",
            minHeight: "0",
            margin: "0",
            padding: `${capturePaddingPx}px`,
            boxSizing: "border-box",
            overflow: "visible",
            backgroundColor,
          });
          fitPreservedMedia(clonedTarget, preservedMediaFrame);
          onClone?.(clonedDocument, clonedTarget);
        },
      });

      const downloadName = normalizeFilename(filename, outputFormat);
      const blob =
        outputFormat === "png"
          ? await canvasToBlob(canvas)
          : await canvasToPdfBlob({
              canvas,
              filename: downloadName,
              pageFormat,
              orientation,
              marginMm,
              footerText,
              showPageNumbers,
            });

      downloadBlob(blob, downloadName);
      onDownloaded?.({ filename: downloadName, blob, format: outputFormat });
    } catch (error) {
      console.error("Document download failed:", error);
      onError?.(error);
      if (!onError) window.alert("Failed to download the document.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <button
      type="button"
      className={className}
      onClick={handleDownload}
      disabled={disabled || isDownloading}
    >
      {isDownloading ? "Preparing PDF..." : label}
    </button>
  );
};

export default DocumentDownloadButton;
